import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DesignTestInputDto,
  DesignTestOutputDto
} from '../../../models/job-request-iterations-dto.interface';

interface WbmChemistryReportValues {
  filtrateAcidConcentration?: number;
  filtrateAcidMfVolume?: number;
  filtrateAcidPfVolume?: number;
  filtrateEdtaCalciumVolume?: number;
  filtrateEdtaConcentration?: number;
  filtrateEdtaTotalHardnessVolume?: number;
  filtrateMethyleneBlueDemandVolume?: number;
  filtrateMethyleneBlueSampleVolume?: number;
  filtrateSampleVolume?: number;
  filtrateSilverNitrateConcentration?: number;
  filtrateSilverNitrateVolume?: number;
  ph?: number;
  sandContentPercentByVolume?: number;
  wholeMudAcidConcentration?: number;
  wholeMudAcidVolume?: number;
  wholeMudEdtaConcentration?: number;
  wholeMudEdtaVolume?: number;
  wholeMudSampleVolume?: number;
  wholeMudSilverNitrateConcentration?: number;
  wholeMudSilverNitrateVolume?: number;
}

@Component({
  selector: 'app-wbm-chemistry-test-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './wbm-chemistry-test-report.component.html',
  styleUrl: './wbm-chemistry-test-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WbmChemistryTestReportComponent {
  @Input({ required: true }) test!: DesignTestInputDto;
  @Input({ required: true }) output!: DesignTestOutputDto;

  protected wbmChemistryValues(): WbmChemistryReportValues | null {
    const v = this.derivedValues();

    const filtrateAcidConcentration = this.numberValue(['filtrateAcidConcentration', 'filtrateacidconcentration'], this.output) ?? this.numberValue(['filtrateAcidConcentration'], this.test) ?? v.filtrateAcidConcentration;
    const filtrateAcidMfVolume = this.numberValue(['filtrateAcidMfVolume', 'filtrateacidmfvolume'], this.output) ?? v.filtrateAcidMfVolume;
    const filtrateAcidPfVolume = this.numberValue(['filtrateAcidPfVolume', 'filtrateacidpfvolume'], this.output) ?? v.filtrateAcidPfVolume;
    const filtrateEdtaCalciumVolume = this.numberValue(['filtrateEdtaCalciumVolume', 'filtrateedtacalciumvolume'], this.output) ?? v.filtrateEdtaCalciumVolume;
    const filtrateEdtaConcentration = this.numberValue(['filtrateEdtaConcentration', 'filtrateedtaconcentration'], this.output) ?? v.filtrateEdtaConcentration;
    const filtrateEdtaTotalHardnessVolume = this.numberValue(['filtrateEdtaTotalHardnessVolume', 'filtrateedtatotalhardnessvolume'], this.output) ?? v.filtrateEdtaTotalHardnessVolume;
    const filtrateMethyleneBlueDemandVolume = this.numberValue(['filtrateMethyleneBlueDemandVolume', 'filtratemethylenebluedemandvolume'], this.output) ?? v.filtrateMethyleneBlueDemandVolume;
    const filtrateMethyleneBlueSampleVolume = this.numberValue(['filtrateMethyleneBlueSampleVolume', 'filtratemethylenebluesamplevolume'], this.output) ?? v.filtrateMethyleneBlueSampleVolume;
    const filtrateSampleVolume = this.numberValue(['filtrateSampleVolume', 'filtratesamplevolume'], this.output) ?? v.filtrateSampleVolume;
    const filtrateSilverNitrateConcentration = this.numberValue(['filtrateSilverNitrateConcentration', 'filtratesilvernitrateconcentration'], this.output) ?? v.filtrateSilverNitrateConcentration;
    const filtrateSilverNitrateVolume = this.numberValue(['filtrateSilverNitrateVolume', 'filtratesilvernitratevolume'], this.output) ?? v.filtrateSilverNitrateVolume;
    const ph = this.numberValue(['ph'], this.output) ?? v.ph;
    const sandContentPercentByVolume = this.numberValue(['sandContentPercentByVolume', 'sandcontentpercentbyvolume'], this.output) ?? v.sandContentPercentByVolume;

    const wholeMudAcidConcentration = this.numberValue(['wholeMudAcidConcentration', 'wholemudacidconcentration'], this.output) ?? v.wholeMudAcidConcentration;
    const wholeMudAcidVolume = this.numberValue(['wholeMudAcidVolume', 'wholemudacidvolume'], this.output) ?? v.wholeMudAcidVolume;
    const wholeMudEdtaConcentration = this.numberValue(['wholeMudEdtaConcentration', 'wholemudedtaconcentration'], this.output) ?? v.wholeMudEdtaConcentration;
    const wholeMudEdtaVolume = this.numberValue(['wholeMudEdtaVolume', 'wholemudedtavolume'], this.output) ?? v.wholeMudEdtaVolume;
    const wholeMudSampleVolume = this.numberValue(['wholeMudSampleVolume', 'wholemudsamplevolume'], this.output) ?? v.wholeMudSampleVolume;
    const wholeMudSilverNitrateConcentration = this.numberValue(['wholeMudSilverNitrateConcentration', 'wholemudsilvernitrateconcentration'], this.output) ?? v.wholeMudSilverNitrateConcentration;
    const wholeMudSilverNitrateVolume = this.numberValue(['wholeMudSilverNitrateVolume', 'wholemudsilvernitratevolume'], this.output) ?? v.wholeMudSilverNitrateVolume;

    if ([filtrateAcidConcentration, filtrateAcidMfVolume, filtrateAcidPfVolume, filtrateEdtaCalciumVolume, filtrateEdtaConcentration, filtrateEdtaTotalHardnessVolume, filtrateMethyleneBlueDemandVolume, filtrateMethyleneBlueSampleVolume, filtrateSampleVolume, filtrateSilverNitrateConcentration, filtrateSilverNitrateVolume, ph, sandContentPercentByVolume, wholeMudAcidConcentration, wholeMudAcidVolume, wholeMudEdtaConcentration, wholeMudEdtaVolume, wholeMudSampleVolume, wholeMudSilverNitrateConcentration, wholeMudSilverNitrateVolume].every(x => x === undefined)) return null;

    return {
      filtrateAcidConcentration,
      filtrateAcidMfVolume,
      filtrateAcidPfVolume,
      filtrateEdtaCalciumVolume,
      filtrateEdtaConcentration,
      filtrateEdtaTotalHardnessVolume,
      filtrateMethyleneBlueDemandVolume,
      filtrateMethyleneBlueSampleVolume,
      filtrateSampleVolume,
      filtrateSilverNitrateConcentration,
      filtrateSilverNitrateVolume,
      ph,
      sandContentPercentByVolume,
      wholeMudAcidConcentration,
      wholeMudAcidVolume,
      wholeMudEdtaConcentration,
      wholeMudEdtaVolume,
      wholeMudSampleVolume,
      wholeMudSilverNitrateConcentration,
      wholeMudSilverNitrateVolume
    };
  }

  protected formatDecimal(value: number | undefined): string {
    return value === undefined ? '—' : value.toFixed(2);
  }

  protected formatWholeNumber(value: number | undefined): string {
    return value === undefined ? '—' : new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
  }

  private derivedValues(): WbmChemistryReportValues {
    // Try to extract from parsed XML/content if present
    const v: WbmChemistryReportValues = {};
    const map = (names: string[]) => this.firstNumber(names, this.output, this.test);

    v.filtrateAcidConcentration = map(['filtrateAcidConcentration', 'filtrateacidconcentration']);
    v.filtrateAcidMfVolume = map(['filtrateAcidMfVolume', 'filtrateacidmfvolume']);
    v.filtrateAcidPfVolume = map(['filtrateAcidPfVolume', 'filtrateacidpfvolume']);
    v.filtrateEdtaCalciumVolume = map(['filtrateEdtaCalciumVolume', 'filtrateedtacalciumvolume']);
    v.filtrateEdtaConcentration = map(['filtrateEdtaConcentration', 'filtrateedtaconcentration']);
    v.filtrateEdtaTotalHardnessVolume = map(['filtrateEdtaTotalHardnessVolume', 'filtrateedtatotalhardnessvolume']);
    v.filtrateMethyleneBlueDemandVolume = map(['filtrateMethyleneBlueDemandVolume', 'filtratemethylenebluedemandvolume']);
    v.filtrateMethyleneBlueSampleVolume = map(['filtrateMethyleneBlueSampleVolume', 'filtratemethylenebluesamplevolume']);
    v.filtrateSampleVolume = map(['filtrateSampleVolume', 'filtratesamplevolume']);
    v.filtrateSilverNitrateConcentration = map(['filtrateSilverNitrateConcentration', 'filtratesilvernitrateconcentration', 'filtrateSilverNitrateConcentration']);
    v.filtrateSilverNitrateVolume = map(['filtrateSilverNitrateVolume', 'filtratesilvernitratevolume']);
    v.ph = map(['ph']);
    v.sandContentPercentByVolume = map(['sandContentPercentByVolume', 'sandcontentpercentbyvolume']);

    v.wholeMudAcidConcentration = map(['wholeMudAcidConcentration', 'wholemudacidconcentration']);
    v.wholeMudAcidVolume = map(['wholeMudAcidVolume', 'wholemudacidvolume']);
    v.wholeMudEdtaConcentration = map(['wholeMudEdtaConcentration', 'wholemudedtaconcentration']);
    v.wholeMudEdtaVolume = map(['wholeMudEdtaVolume', 'wholemudedtavolume']);
    v.wholeMudSampleVolume = map(['wholeMudSampleVolume', 'wholemudsamplevolume']);
    v.wholeMudSilverNitrateConcentration = map(['wholeMudSilverNitrateConcentration', 'wholemudsilvernitrateconcentration']);
    v.wholeMudSilverNitrateVolume = map(['wholeMudSilverNitrateVolume', 'wholemudsilvernitratevolume']);

    return v;
  }

  private firstNumber(names: string[], ...sources: Array<DesignTestInputDto | DesignTestOutputDto>): number | undefined {
    for (const source of sources) {
      const v = this.numberValue(names, source);
      if (v !== undefined) return v;
    }
    return undefined;
  }

  private numberValue(names: string[], source: DesignTestInputDto | DesignTestOutputDto): number | undefined {
    const v = this.value(names, source);
    return this.toNumber(v);
  }

  private value(names: string[], source: DesignTestInputDto | DesignTestOutputDto): string | null {
    const acceptedNames = new Set(names.map(n => this.normalize(n)));
    const fields = 'parsedValues' in source ? source.parsedValues : source.parsedInputValues;
    const parsedField = fields.find(field => acceptedNames.has(this.normalize(field.label)));
    return parsedField?.value?.trim() || this.xmlValue(source.content, acceptedNames);
  }

  private xmlValue(content: string | null, names: Set<string>): string | null {
    if (!content) return null;
    const doc = new DOMParser().parseFromString(content, 'application/xml');
    if (doc.querySelector('parsererror')) return null;
    for (const element of Array.from(doc.getElementsByTagName('*'))) {
      if (element.localName.toLowerCase() === 'parameter') {
        if (!names.has(this.normalize(element.getAttribute('key')))) continue;
      } else if (!names.has(this.normalize(element.localName))) {
        continue;
      }
      if (element.children.length === 0) {
        const value = element.textContent?.trim();
        if (value) return value;
      }
    }
    return null;
  }

  private normalize(value: string | null | undefined): string {
    return (value ?? '').replace(/[\s_\-/,().:%]+/g, '').toLowerCase();
  }

  private toNumber(value: string | null): number | undefined {
    if (!value) return undefined;
    const parsed = Number(value.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}
